/**
 * As edições de uma versão anterior (E160.F3, ADR-0110 §2.3): a análise mudou depois delas, e cada
 * uma se reaplica à versão vigente, uma a uma. A de um texto original que mudou não se reaplica.
 */
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  EdicaoDesatualizadaData,
  EdicaoDoLaudoData,
  TrechoNaTelaData,
} from '@/lib/correcaoDoRascunho';
import { planoDoMarkup } from '@/lib/markupDaEdicao';

const t = ptBR.correcaoDoRascunho;

/** A seção pela chave do trecho (`p.<seção>.<sha>`, `nota.<seção>`, `r.<n>.<campo>` no mapa). */
function secaoDaChave(trechoId: string): string {
  const partes = trechoId.split('.');
  if (partes[0] === 'r') return 'mapa';
  return (partes[0] === 'p' || partes[0] === 'nota' ? partes[1] : partes[0]) ?? '';
}

/** O trecho como a tela o mostra; o que mudou de texto sai pela seção, nunca pela chave crua. */
function rotuloDaEdicao(edicao: EdicaoDoLaudoData, trechos: TrechoNaTelaData[]): string {
  const trecho = trechos.find((x) => x.id === edicao.trecho_id);
  if (trecho) return `${trecho.titulo_da_secao} · ${trecho.rotulo}`;
  const secao = secaoDaChave(edicao.trecho_id);
  const daSecao = trechos.find((x) => x.secao === secao);
  return daSecao
    ? `${daSecao.titulo_da_secao} · ${t.desatualizadas.trechoAnterior}`
    : t.desatualizadas.trechoAnterior;
}

export interface EdicoesDesatualizadasProps {
  desatualizadas: EdicaoDesatualizadaData[];
  trechos: TrechoNaTelaData[];
  bloqueado: boolean;
  salvando: boolean;
  onReaplicar: (edicaoId: string) => void;
}

export function EdicoesDesatualizadas({
  desatualizadas,
  trechos,
  bloqueado,
  salvando,
  onReaplicar,
}: EdicoesDesatualizadasProps) {
  if (desatualizadas.length === 0) return null;
  return (
    <section aria-labelledby="edicoes-desatualizadas" data-testid="edicoes-desatualizadas">
      <h3 id="edicoes-desatualizadas" className="text-body-md font-semibold text-fg">
        {t.desatualizadas.titulo}
      </h3>
      <p className="max-w-prose text-caption text-fg-muted">{t.desatualizadas.ajuda}</p>
      <ul className="mt-sc-2 space-y-sc-2">
        {desatualizadas.map(({ edicao, motivo }) => {
          return (
            <li
              key={edicao.id}
              className="border-t border-border pt-sc-2 text-body-sm"
              data-testid="desatualizada"
              data-motivo={motivo}
            >
              <p className="font-semibold text-fg">{rotuloDaEdicao(edicao, trechos)}</p>
              <p className="whitespace-pre-line text-fg" data-testid="texto-anterior">
                {edicao.texto !== null ? planoDoMarkup(edicao.texto) : t.acao[edicao.acao]}
              </p>
              <p className="text-caption text-fg-muted">
                {t.desatualizadas.fundamento.replace('{fundamento}', edicao.fundamento)}
              </p>
              {motivo === 'trecho_mudou' ? (
                <p className="text-caption text-warning-700">{t.desatualizadas.trechoMudou}</p>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={bloqueado || salvando}
                  onClick={() => onReaplicar(edicao.id)}
                  data-testid="reaplicar"
                >
                  {t.desatualizadas.reaplicar}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
